import { Buyer, ITextBlock } from "@hooks/useForm/types";
import {
    getAccessToken,
    getLocale,
    getTestVariantId,
    setTestVariantId,
} from "@lib/shared/cookies";
import { Step, ServerRequest } from "@lib/shared/types";
import HTTP, { apiHandler } from "@services/http.service";
import { mappingFormInfo } from "@lib/serverUtils";
import { AxiosRequestConfig } from "axios";
import { ServerResponse } from "http";
import { LRUCache } from "lru-cache";

interface GlobalWithCache {
    __formCache?: LRUCache<string, { form: DomainForm | null }>;
}

const g = globalThis as unknown as GlobalWithCache;

const formCache =
    g.__formCache ??
    (g.__formCache = new LRUCache<string, { form: DomainForm | null }>({
        max: 500,
        ttl: 1000 * 60 * 60, // 60 minutes
    }));

interface DomainFormResponse {
    success: boolean;
    data: DomainForm | null;
}
export interface TestConfig {
    config?: {
        css?: string;
        formId?: number;
        formInsideModal?: boolean;
        mergeFirstInput?: boolean;
        templateId?: number;
    };
    variantId?: number;
}
export interface supportedCountry {
    id: number;
    label: string;
    value: string;
}

export interface DomainForm {
    title: string;
    description: string;
    steps: Step[];
    id: number;
    stepToSendingData: number | null;
    successMessage: string;
    errorMessage: string;
    categorySlug: string;
    domainFormId: number | null;
    listingId: number;
    listing: { id: number; slug: string; modelType: string };
    testConfig?: TestConfig;
    supportedCountries: supportedCountry[];
    tCPAMessages?: { TCPAMessage: string }[];
    categoryName?: string;
    textBlocks: ITextBlock[];
    stepToFetchBuyers: number | null;
    defaultBuyers: Buyer[];
}

export const getDomainForm = async ({
    req,
    res,
    categorySlug,
}: {
    req?: ServerRequest;
    res?: ServerResponse;
    categorySlug: string;
}): Promise<{ data: DomainForm | null; error: Error | null }> => {
    const accessToken = req ? req.accessToken : getAccessToken();
    const variantId = getTestVariantId("form", categorySlug, req, res);
    const host = req?.headers?.host ?? "";
    const locale = req ? req.locale ?? "en" : getLocale();

    // Build cache key
    const formCacheKey = `form:${host}:${categorySlug}:${String(
        locale,
    )}:${String(variantId)}`;

    // Check cache first (only for server-side requests)
    if (req) {
        const cached = formCache.get(formCacheKey);
        if (cached) {
            const cachedForm = cached.form;
            // Set test variant ID from cached data
            if (cachedForm) {
                setTestVariantId(
                    "form",
                    String(cachedForm.testConfig?.variantId),
                    cachedForm.categorySlug ?? null,
                    req,
                    res,
                );
            }
            return {
                data: cachedForm,
                error: null,
            };
        }
    }

    const parsedVariantId = parseInt(variantId as string, 10);

    const numberOfServices = categorySlug.split(",").length;
    const endPoint =
        numberOfServices > 1 ? "/api/web/v1/forms/multi" : "/api/web/v3/forms";

    const config: AxiosRequestConfig = {
        method: "get",
        url: req
            ? `${endPoint}/${categorySlug}${
                  !isNaN(parsedVariantId)
                      ? `?variantId=${String(parsedVariantId)}`
                      : ""
              }`
            : `/api/form/${categorySlug}`,
        headers: {
            "X-DOMAIN-NAME": `${req ? req?.headers?.host ?? "" : ""}`,
            Authorization: `Bearer ${accessToken as string}`,
            "X-Language": req ? req.locale ?? "en" : getLocale(),
        },
    };
    if (req) {
        const { data: formResponse, error } =
            await apiHandler<DomainFormResponse>(() => HTTP.server(config));

        const formData = formResponse?.data ?? null;

        if (formResponse) {
            setTestVariantId(
                "form",
                String(formData?.testConfig?.variantId),
                formData?.categorySlug ?? null,
                req,
                res,
            );

            // Cache the result
            if (formData && !error) {
                formCache.set(formCacheKey, {
                    form: mappingFormInfo(formData),
                });
            }
        }

        return {
            data: mappingFormInfo(formData),
            error,
        };
    } else {
        const { data: formResponse, error } =
            await apiHandler<DomainFormResponse>(() => HTTP.client(config));

        return {
            data: formResponse?.data ?? null,
            error,
        };
    }
};
